import React, { useState } from "react";
import axios from "axios";
import { Navigate, useNavigate } from "react-router-dom";
import { Shield, Eye, EyeOff, Loader, ChevronLeft } from "lucide-react";
import { useAuth, formatApiError } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PlatformLoginPage() {
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user?.is_platform_admin) return <Navigate to="/platform/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await axios.post(`${API}/platform/login`, { email, password }, { withCredentials: true });
      setUser({ ...r.data, is_platform_admin: true });
      navigate("/platform/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Anmeldung fehlgeschlagen");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: "radial-gradient(circle at 30% 20%, hsl(18,72%,56%) 0%, transparent 50%), radial-gradient(circle at 70% 80%, hsl(220,40%,40%) 0%, transparent 50%)"
      }} />
      <div className="relative z-10 w-full max-w-sm mx-4">
        <button onClick={() => navigate("/login")} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="platform-back-to-login">
          <ChevronLeft size={14} /> Zurück zum Workspace-Login
        </button>
        <div className="bg-card border border-border p-8 shadow-xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-9 h-9 bg-foreground flex items-center justify-center">
              <Shield size={18} className="text-background" />
            </div>
            <div>
              <span className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>DATON</span>
              <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Platform Admin</p>
            </div>
          </div>

          <h1 className="text-xl font-semibold mb-1">Platform-Login</h1>
          <p className="text-sm text-muted-foreground mb-6">Verwaltung aller Workspaces — kein Zugriff auf Kundendaten</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">E-Mail</label>
              <input data-testid="platform-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
                className="w-full px-3 py-2.5 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1.5">Passwort</label>
              <div className="relative">
                <input data-testid="platform-password" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)} required
                  className="w-full px-3 py-2.5 pr-10 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {error && <div data-testid="platform-error" className="text-sm text-destructive bg-destructive/10 px-3 py-2 border border-destructive/20">{error}</div>}
            <button data-testid="platform-submit" type="submit" disabled={loading}
              className="w-full py-2.5 bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
              {loading && <Loader size={15} className="animate-spin" />} {loading ? "Anmelden..." : "Anmelden"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
